import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PWAManager } from './components/PWAManager';
import { SnareForm } from './features/snares/SnareForm';
import { Groups } from './pages/Groups';
import { Map } from './pages/Map';
import { Database } from './pages/Database';
import { Profile } from './pages/Profile';
import { Landing } from './pages/Landing';
import { AuthSuccess } from './pages/AuthSuccess';
import { Admin } from './pages/Admin';
import { BottomNav } from './components/BottomNav';
import { isMobileDevice } from './lib/device';
import { getMyGroups } from './lib/api/groups';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'snares' | 'map' | 'database' | 'groups' | 'profile' | 'admin'>('snares');
  const [pwaReady, setPwaReady] = useState(false);
  const [initialRoute, setInitialRoute] = useState<string | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [selectedRecordType, setSelectedRecordType] = useState<'snare' | 'carcass' | null>(null);

  const isMobile = isMobileDevice();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  function handleNavigateToMap(recordId: string, recordType: 'snare' | 'carcass') {
    setSelectedRecordId(recordId);
    setSelectedRecordType(recordType);
    setActiveTab('map');
  }

  // Handle auth-success route
  if (window.location.pathname === '/auth-success') {
    return <AuthSuccess />;
  }

  // Determine initial route based on device and user groups
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    async function determineInitialRoute() {
      if (isMobile) {
        // Mobile users: check their groups
        try {
          const groups = await getMyGroups();
          const approvedGroups = groups.filter(g => g.status === 'approved');

          if (approvedGroups.length === 0) {
            // No approved groups → groups page
            setInitialRoute('groups');
            setActiveTab('groups');
          } else if (approvedGroups.length === 1) {
            // One approved group → snares page
            setInitialRoute('snares');
            setActiveTab('snares');
          } else {
            // Multiple approved groups → groups page
            setInitialRoute('groups');
            setActiveTab('groups');
          }
        } catch (error) {
          console.error('[APP] Error fetching groups:', error);
          setInitialRoute('groups');
          setActiveTab('groups');
        }
      } else {
        // Desktop users → database page
        setInitialRoute('database');
        setActiveTab('database');
      }
    }

    determineInitialRoute();
  }, [isAuthenticated, user, isMobile]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → show landing page
  if (!isAuthenticated) {
    return <Landing />;
  }

  // Authenticated but PWA checks not complete → show PWA manager
  if (!pwaReady) {
    return <PWAManager onReady={() => setPwaReady(true)} />;
  }

  // Authenticated and PWA ready → show app
  // Wait for initial route to be determined
  if (!initialRoute) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-foreground">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Header with logo */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="6" />
              <circle cx="12" cy="12" r="2" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold">Trapp</h1>
          </div>
        </div>
        {user && !isMobile && (
          <nav className="flex items-center gap-1">
            {([
              { id: 'database', label: 'Database' },
              { id: 'map', label: 'Map' },
              { id: 'groups', label: 'Groups' },
              ...(isAdmin ? [{ id: 'admin', label: 'Admin' }] : []),
              { id: 'profile', label: 'Profile' },
            ] as { id: typeof activeTab; label: string }[]).map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        )}
        {user && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user.firstName || user.email}
            </span>
            {user.profileImageUrl && (
              <img
                src={user.profileImageUrl}
                alt={user.firstName || user.email}
                className="w-8 h-8 rounded-full"
              />
            )}
          </div>
        )}
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-hidden">
        {activeTab === 'snares' && (
          <SnareForm
            userId={user!.id}
            onSuccess={() => console.log('[APP] Snare logged successfully')}
          />
        )}
        {activeTab === 'map' && <Map selectedRecordId={selectedRecordId} selectedRecordType={selectedRecordType} />}
        {activeTab === 'database' && <Database onNavigateToMap={handleNavigateToMap} />}
        {activeTab === 'groups' && <Groups />}
        {activeTab === 'profile' && <Profile />}
        {activeTab === 'admin' && <Admin />}
      </main>

      {/* Bottom navigation (mobile) or no nav (desktop shows tabs in header) */}
      {isMobile && (
        <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />
      )}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
